
import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import AppSidebar from './AppSidebar';
import SystemAlertsBell from './SystemAlertsBell';
import { useAuth } from '@/contexts/AuthContext';
import AccountSwitcher from '@/components/AccountSwitcher';
import { Search, Bell, Eye, Clock, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const CLIENTE_VISUAL_ALLOWED_PATHS = ['/dashboard', '/client-schedule', '/disparo-avulso', '/email'];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user?.role === 'cliente_visual' && !CLIENTE_VISUAL_ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background overflow-hidden">
        <AppSidebar />
        <main className="flex-1 flex flex-col min-w-0">
          {/* Header Superior Moderno - Estilo Opensheets */}
          <header className="bg-white border-b border-border/40 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              {/* Lado Esquerdo - Trigger da Sidebar (mobile) */}
              <div className="flex items-center gap-4">
                <SidebarTrigger className="lg:hidden" />
              </div>

              {/* Centro - Barra de Pesquisa */}
              <div className="flex-1 max-w-md mx-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Buscar..."
                    className="pl-9 h-9 bg-muted/50 border-border/50 focus-visible:ring-ring/50 rounded-md text-sm"
                  />
                </div>
              </div>

              {/* Lado Direito - Ícones de Ação */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <AccountSwitcher />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-md hover:bg-muted/80"
                  title="Histórico"
                >
                  <Clock className="h-4 w-4 text-foreground" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-md hover:bg-muted/80"
                  title="Visualizar"
                >
                  <Eye className="h-4 w-4 text-foreground" />
                </Button>

                {user?.role === 'admin' && <SystemAlertsBell />}

                {/* Perfil do Usuário */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted/80 transition-colors cursor-pointer">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-foreground leading-tight font-funnel-display">{user?.name}</p>
                    <p className="text-xs text-muted-foreground leading-tight font-funnel-display">
                      {user?.role === 'admin' ? 'Super Admin' : user?.role === 'cliente_visual' ? 'Cliente visual' : user?.role === 'analista' ? 'Analista' : 'Cliente Admin'}
                    </p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-md hover:bg-destructive/10 hover:text-destructive"
                  title="Sair"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </header>
          
          {/* Área de Conteúdo */}
          <div className="flex-1 p-6 overflow-auto bg-background">
            <div className="max-w-full">
              {children}
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default Layout;
